import * as React from 'react';
import { ICapacite, IBac } from '../../../models';
import { getContentColor } from '../../shared/ProductColors';
import styles from './KpiCards.module.scss';

export interface IKpiCardsProps {
  capacites: ICapacite[];
  bacs: IBac[];
  clientName: string;
}

interface ProductBreakdown {
  produit: string;
  volume: number;
  pct: number;
  color: string;
}

export const KpiCards: React.FC<IKpiCardsProps> = ({ capacites, bacs }) => {
  const stats = React.useMemo(() => {
    const bacById = new Map(bacs.map(b => [b.Id, b]));

    const depots = new Set(capacites.map(c => c.Depot));
    const bacsSet = new Set(capacites.map(c => `${c.DepotId}-${c.Bac}`));

    let totalAlloue = 0;
    let totalRestrictionAmount = 0; // positive number = volume removed by restrictions

    for (const c of capacites) {
      totalAlloue += c.VolumeAlloue;
      const bac = bacById.get(c.BacId);
      // bac.RestrictionClients is negative (e.g. -0.3 = -30%). We turn it
      // into a positive "restricted volume" via Math.abs().
      if (bac && bac.RestrictionClients) {
        totalRestrictionAmount += c.VolumeAlloue * Math.abs(bac.RestrictionClients);
      }
    }

    const totalAvailable = Math.max(0, totalAlloue - totalRestrictionAmount);
    const restrictionPct = totalAlloue > 0 ? totalRestrictionAmount / totalAlloue : 0;

    const byProduct: Record<string, number> = {};
    capacites.forEach(c => {
      byProduct[c.Produit] = (byProduct[c.Produit] || 0) + c.VolumeAlloue;
    });

    const products: ProductBreakdown[] = Object.entries(byProduct)
      .sort((a, b) => b[1] - a[1])
      .map(([produit, volume]) => ({
        produit,
        volume,
        pct: totalAlloue > 0 ? volume / totalAlloue : 0,
        color: getContentColor(produit).background,
      }));

    return {
      nbDepots: depots.size,
      nbBacs: bacsSet.size,
      totalAlloue,
      totalRestrictionAmount,
      totalAvailable,
      restrictionPct,
      products,
    };
  }, [capacites, bacs]);

  const fmt = (n: number): string => Math.round(n).toLocaleString('fr-FR');

  return (
    <div className={styles.grid}>
      <div className={styles.card} style={{ animationDelay: '0s' }}>
        <div className={styles.cardIcon}>&#9981;</div>
        <div className={styles.cardBody}>
          <div className={styles.cardValue}>
            {fmt(stats.totalAlloue)}
            <span className={styles.cardUnit}>m³</span>
          </div>
          <div className={styles.cardLabel}>Volume total alloué</div>
        </div>
      </div>

      <div className={`${styles.card} ${styles.cardRestriction}`} style={{ animationDelay: '0.04s' }}>
        <div className={styles.cardIcon}>&#9888;</div>
        <div className={styles.cardBody}>
          <div className={styles.cardValue}>
            {stats.totalRestrictionAmount > 0 ? '−' : ''}{fmt(stats.totalRestrictionAmount)}
            <span className={styles.cardUnit}>m³</span>
          </div>
          <div className={styles.cardLabel}>
            Total restrictions
            {stats.restrictionPct > 0 && (
              <span className={styles.cardSubValue}>
                {' '}&middot; {Math.round(stats.restrictionPct * 100)}% restreint
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={`${styles.card} ${styles.cardAvailable}`} style={{ animationDelay: '0.08s' }}>
        <div className={styles.cardIcon}>&#10004;</div>
        <div className={styles.cardBody}>
          <div className={styles.cardValue}>
            {fmt(stats.totalAvailable)}
            <span className={styles.cardUnit}>m³</span>
          </div>
          <div className={styles.cardLabel}>Alloué disponible (net)</div>
        </div>
      </div>

      <div className={styles.card} style={{ animationDelay: '0.12s' }}>
        <div className={styles.cardIcon}>&#127981;</div>
        <div className={styles.cardBody}>
          <div className={styles.cardValue}>{stats.nbDepots}</div>
          <div className={styles.cardLabel}>Dépôts</div>
        </div>
      </div>

      <div className={styles.card} style={{ animationDelay: '0.16s' }}>
        <div className={styles.cardIcon}>&#9878;</div>
        <div className={styles.cardBody}>
          <div className={styles.cardValue}>{stats.nbBacs}</div>
          <div className={styles.cardLabel}>Bacs</div>
        </div>
      </div>

      <div className={styles.card + ' ' + styles.cardWide} style={{ animationDelay: '0.2s' }}>
        <div className={styles.cardBody}>
          <div className={styles.cardLabel} style={{ marginBottom: 8 }}>Répartition produits</div>
          <div className={styles.barContainer}>
            {stats.products.map(p => (
              <div
                key={p.produit}
                className={styles.barSegment}
                style={{ width: `${Math.max(p.pct * 100, 3)}%`, backgroundColor: p.color }}
                title={`${p.produit}: ${fmt(p.volume)} m³ (${Math.round(p.pct * 100)}%)`}
              />
            ))}
          </div>
          <div className={styles.barLegend}>
            {stats.products.map(p => (
              <div key={p.produit} className={styles.barLegendItem}>
                <span className={styles.barLegendDot} style={{ backgroundColor: p.color }} />
                <span>{p.produit}</span>
                <span className={styles.barLegendPct}>{Math.round(p.pct * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
